// Brand kit organizace a průvodce po založení firmy (docs/PLAN.md 5.1): služby, ceník, slogan,
// oblast, barva, logo; stav karty „První kroky“ na Dnes. Chyby se vyhazují, view je překládá.
import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { BrandKit, BrandKitInput, OnboardingStatus, ServiceItem } from '@wac-buddy/shared'
import * as brandApi from '@/api/brand'
import { uploadOrgFile } from '@/api/attachments'
import { useAuthStore } from './auth'

export type BrandStatus = 'idle' | 'loading' | 'ready' | 'error'

/** Uložený brand kit jako tělo PUT (null se mění na prázdný řetězec). */
export function kitToInput(kit: BrandKit): BrandKitInput {
  return {
    services: kit.services,
    slogan: kit.slogan ?? '',
    area: kit.area ?? '',
    primaryColor: kit.primaryColor ?? '',
    logoKey: kit.logoKey ?? '',
    teamPhotoKeys: kit.teamPhotoKeys,
    forbiddenWords: kit.forbiddenWords,
  }
}

export const useBrandStore = defineStore('brand', () => {
  const kit = ref<BrandKit | null>(null)
  const template = ref<ServiceItem[] | null>(null)
  const status = ref<BrandStatus>('idle')
  const saving = ref(false)
  const onboarding = ref<OnboardingStatus | null>(null)

  async function load(): Promise<void> {
    status.value = 'loading'
    try {
      kit.value = await brandApi.getBrandKit()
      status.value = 'ready'
    } catch {
      status.value = 'error'
    }
  }

  /** Šablona služeb podle oboru firmy (při chybě prázdný seznam, průvodce jde dál). */
  async function loadTemplate(): Promise<void> {
    try {
      template.value = await brandApi.getBrandTemplate()
    } catch {
      template.value = []
    }
  }

  /** Uloží celý brand kit: změněná pole se sloučí s tím, co je uložené. Vyhazuje při chybě. */
  async function save(patch: Partial<BrandKitInput>): Promise<BrandKit> {
    if (!kit.value) await load()
    if (!kit.value) throw new Error('brand kit not loaded')
    saving.value = true
    try {
      kit.value = await brandApi.putBrandKit({ ...kitToInput(kit.value), ...patch })
      return kit.value
    } finally {
      saving.value = false
    }
  }

  async function complete(): Promise<void> {
    await brandApi.completeOnboarding()
    if (kit.value) kit.value = { ...kit.value, completedAt: new Date().toISOString() }
  }

  /** Logo: podepsané nahrání do úložiště, příloha entity org, pak klíč do brand kitu. */
  async function uploadLogo(file: File): Promise<BrandKit> {
    const orgId = useAuthStore().me?.org?.id
    if (!orgId) throw new Error('no organization')
    const key = await uploadOrgFile(orgId, file)
    return save({ logoKey: key })
  }

  function removeLogo(): Promise<BrandKit> {
    return save({ logoKey: '' })
  }

  async function loadOnboarding(): Promise<void> {
    try {
      onboarding.value = await brandApi.getOnboarding()
    } catch {
      /* karta První kroky je jen bonus, Dnes funguje bez ní */
    }
  }

  async function dismissOnboarding(): Promise<void> {
    await brandApi.dismissOnboardingCard()
    if (onboarding.value) onboarding.value = { ...onboarding.value, dismissed: true }
  }

  function invalidate(): void {
    kit.value = null
    template.value = null
    status.value = 'idle'
    onboarding.value = null
  }

  return {
    kit,
    template,
    status,
    saving,
    onboarding,
    load,
    loadTemplate,
    save,
    complete,
    uploadLogo,
    removeLogo,
    loadOnboarding,
    dismissOnboarding,
    invalidate,
  }
})
